/**
 * ORDEM CANONICA DE STATUS + SENTINELAS DE UI da aba Processos.
 *
 * Portado do prototipo (`sistema/index.html`), onde a regra nasceu de um pedido
 * explicito: "nao crie mais um padrao, crie uma funcao de agrupar status e reutilize
 * em todos os lugares". Este modulo e' a UNICA fonte dessas ordens — card Fila, popups
 * e dedup de grupo importam daqui, nunca redeclaram.
 *
 * Sao DUAS ordens, com papeis diferentes e divergentes DE PROPOSITO: ORDEM_STATUS
 * (dedup, `done` vence) e ORDEM_EXIBICAO (lista visivel, falha primeiro). Cada uma
 * explica abaixo por que existe — nao unifique sem ler as duas.
 */

import type { StatusEsteira } from '../api';

/**
 * Prioridade de DEDUP: na dedup por empresa dentro do grupo, MENOR indice ganha —
 * `done` vence `err`, que vence `warn`... A semantica esta documentada em
 * `derivarProcessos.ts` (`empresasDoGrupoComStatus`): se a empresa concluiu em
 * QUALQUER bot do grupo, o no'/popup a conta como concluida; o erro do outro bot
 * sobrevive em `ocorrenciaEmOutroBot`. Mexer aqui muda a contagem do no'.
 *
 * NAO e' a ordem de EXIBICAO — veja ORDEM_EXIBICAO logo abaixo.
 */
export const ORDEM_STATUS: Record<StatusEsteira, number> = {
  done: 0,
  // `skip` DEPOIS de done e ANTES de err (24/09), igual ao BFF: a empresa que
  // concluiu num bot e foi pulada noutro CONCLUIU; a que foi pulada num e falhou
  // noutro FALHOU. Pular nao e' desfecho ruim — e' ausencia de trabalho a fazer.
  skip: 1,
  err: 2,
  warn: 3,
  run: 4,
  wait: 5,
};

/**
 * Prioridade de EXIBICAO em lista: o que EXIGE ACAO primeiro (falha, alerta),
 * depois o que esta em andamento, e o `done` por ultimo.
 *
 * POR QUE duas ordens, divergindo de proposito:
 * - a de DEDUP (ORDEM_STATUS) responde "qual status representa esta empresa" e
 *   precisa que `done` venca — o no' conta como concluida a empresa que concluiu;
 * - a de EXIBICAO responde "o que o usuario precisa ver primeiro", e ai' `done` e'
 *   justamente o que menos importa.
 * Unificar as duas quebra um lado: com `done` no topo, um no' com 282 concluidas
 * empurra a 1a falha para a posicao 283 (~15.000px de rolagem dentro do popup) e
 * o usuario le' "22 falhas" no cabecalho sem achar nenhuma na lista — o bug de
 * 11/09, que so' apareceu quando as listas passaram de dezenas para centenas.
 */
export const ORDEM_EXIBICAO: Record<StatusEsteira, number> = {
  err: 0,
  warn: 1,
  run: 2,
  wait: 3,
  // `skip` junto de `done` no fim: nenhum dos dois exige acao. Antes de `done`
  // porque "foi pulada" responde uma duvida que "concluiu" nao levanta.
  skip: 4,
  done: 5,
};

/**
 * "Empresas a processar" e nao "Na fila" (24/09, em TODAS as telas): "fila" e'
 * vocabulario de quem opera o despacho, e a tela e' lida por quem acompanha o
 * trabalho contabil. Exportado porque `vocabularioMacro` reusa o MESMO nome para
 * o grupo `run + wait` da macro — a mesma coisa nao pode ter dois nomes.
 */
export const ROTULO_PENDENTE = 'Empresas a processar';

/** Rotulo e tom visual de cada status — mesmo vocabulario do prototipo. */
export const ST_META: Record<StatusEsteira, { label: string; cor: string }> = {
  done: { label: 'Concluído', cor: 'green' },
  run: { label: 'Executando', cor: 'blue' },
  err: { label: 'Falha', cor: 'red' },
  warn: { label: 'Alerta', cor: 'orange' },
  // "Pendente" (25/09, pedido do dono; era "Pulada"). O bot decidiu que nao havia
  // o que fazer AGORA — sem movimento, sem tributo, ou a obrigacao ja' estava
  // cumprida. Nao e' sucesso (nada foi produzido) nem falha (nada quebrou).
  skip: { label: 'Pendente', cor: 'gray' },
  wait: { label: ROTULO_PENDENTE, cor: 'gray' },
};

/**
 * Ordena qualquer lista de itens com campo `st` pela prioridade de ORDEM_STATUS.
 * Nao muta a lista recebida. Status desconhecido cai no fim (defensivo: o BFF pode
 * ganhar um status novo antes do front).
 */
export const ordenarPorStatus = <T extends { st: StatusEsteira }>(lista: T[]): T[] =>
  lista.slice().sort((a, b) => (ORDEM_STATUS[a.st] ?? 9) - (ORDEM_STATUS[b.st] ?? 9));

/**
 * Mesma mecanica de `ordenarPorStatus` (copia + sort estavel, desconhecido no fim),
 * mas pela ORDEM_EXIBICAO: e' a que as LISTAS visiveis usam (popup e card Fila),
 * para a falha nunca ficar atras de centenas de concluidas.
 */
export const ordenarParaExibicao = <T extends { st: StatusEsteira }>(lista: T[]): T[] =>
  lista.slice().sort((a, b) => (ORDEM_EXIBICAO[a.st] ?? 9) - (ORDEM_EXIBICAO[b.st] ?? 9));

/**
 * Sentinelas de UI — NUNCA sao ids reais de grupo/departamento; so' dao aos orfaos um
 * lugar visual na tela (pedido 02/09: "bots sem grupo aparecem em um no' isolado").
 * Os ids reais vem do BFF como STRING uuid4 hex (32 chars [0-9a-f], gerados pelo mon
 * em bot_organizacao); as sentinelas carregam `__` de proposito, para nunca colidirem.
 */
export const SEM_GRUPO_ID = '__sem_grupo__';
export const SEM_DEPARTAMENTO_ID = '__sem_departamento__';
/**
 * Board "micro" SEM filtro de grupo (pedido 03/09: "tirar o filtro por grupo e ver
 * todos os bots de uma vez") — diferente de grupo null, que volta pra MACRO.
 */
export const TODOS_GRUPOS_ID = '__todos_grupos__';

/** Selecao de grupo no nivel board: id real (string uuid4 hex) ou uma das sentinelas. */
export type GrupoSelecionado = string;

/**
 * Grupo REAL se distingue das sentinelas por EXCLUSAO — nunca por `typeof`: id real e
 * sentinela sao ambos string (o gate `typeof === 'number'` era sempre false com o
 * uuid do backend e deixava o board eternamente vazio).
 */
export const ehGrupoReal = (grupo: GrupoSelecionado): boolean =>
  grupo !== SEM_GRUPO_ID && grupo !== TODOS_GRUPOS_ID;

/**
 * PERCENTUAL DE SUCESSO do no' (30/09, pedido do dono) — "a quantidade de
 * empresas que deram sucesso", no rodape logo ACIMA da barra.
 *
 * REGRA, decidida pelo dono: sucesso e' so' quem CONCLUIU, sobre quem ja' teve
 * DESFECHO. Pendente, na fila e em execucao ficam FORA das duas pontas:
 *
 *     concluidas / (concluidas + falhas)
 *
 * Por que nao o total do no' (a base da barra): a barra responde "quanto do
 * trabalho ja' andou" e por isso precisa do universo inteiro no denominador —
 * quem esta' na fila conta contra o progresso, porque falta fazer. Este numero
 * responde outra pergunta, "do que o bot TERMINOU, quanto deu certo", e ai' a
 * empresa que ele nem chegou a processar nao pode contar contra a taxa. Sao
 * reguas diferentes DE PROPOSITO, e por isso os dois numeros divergem num no'
 * com fila grande — nao e' inconsistencia.
 *
 * O ALERTA nao e' sucesso (decisao do dono no mesmo pedido). Na MACRO ele ja'
 * vem somado dentro de `falhas` (ver `falhasDaMacro`), entao entra no
 * denominador; no board o chamador passa `err` puro e o `warn` fica de fora das
 * duas pontas, como pendente e fila. E' a mesma assimetria que o rodape ja' tem
 * entre os dois niveis desde 25/09, nao uma regra nova.
 *
 * `null` quando NINGUEM teve desfecho ainda (denominador zero): a taxa nao
 * existe, e um "0%" ali seria afirmar fracasso onde nao houve nem tentativa
 * concluida. Quem renderiza decide o que fazer com a ausencia.
 */
export const pctSucesso = (concluidas: number, falhas: number): number | null => {
  const comDesfecho = concluidas + falhas;
  if (comDesfecho <= 0) return null;
  return Math.round((concluidas / comDesfecho) * 100);
};

/**
 * Meta compacta do no' — "✓ N · ✕ N · ⚠ N · ⏭ N · 🚚 N · ⏳ N". So' as partes com
 * valor > 0. ⏭ = PENDENTE (o `skip`), acrescentado em 25/09: sem ele o rodape
 * nunca fechava com o total do no'.
 * ✕ (e nao ⚠) para falha: ⚠ e' RESERVADO para "Alerta" (warn), que tem parte propria.
 * ⏳ = na fila — o MACRO mostra, o board nao (comportamento do prototipo, item 6 da C2).
 *
 * `warns` e' o ultimo parametro DE PROPOSITO (e opcional): os chamadores antigos
 * seguem validos sem mudanca e, sem valor, nenhum ⚠ e' impresso. Ate 12/09 o
 * simbolo estava reservado mas inalcancavel — `ProcessosTab` somava o warn dentro
 * do `done` (`done + contagens.warn`), entao o alerta nunca virava contagem
 * propria e o filtro de status "warning" mostrava um no' que nao citava alerta
 * nenhum. A ordem das partes poe ⚠ DEPOIS do ✕: falha continua sendo o primeiro
 * sinal de acao do no'.
 */
export const emojiMeta = (
  done: number,
  errs: number,
  running: number,
  waiting?: number,
  total?: number,
  // Sem valor default na ASSINATURA (lint `default-param-last`): o fallback mora
  // no corpo, para `warns` poder entrar depois sem reordenar os 3 chamadores.
  vazio?: string,
  warns?: number,
  // PENDENTES (25/09) entram como OBJETO, não como oitavo posicional: sete
  // posições já é mais do que se lê sem contar vírgulas, e o próximo campo
  // repetiria o problema. Os posicionais ficam por retrocompat.
  extra?: { skip?: number },
): string => {
  const partes: string[] = [];
  if (done > 0) partes.push(`✓ ${done}`);
  if (errs > 0) partes.push(`✕ ${errs}`);
  if (warns && warns > 0) partes.push(`⚠ ${warns}`);
  // ⏭ = PULADAS (o `skip`), ENTRE PARÊNTESES porque NÃO é uma parcela da soma
  // (09/10, confusão apontada pelo dono: *"o skip deixa confuso com base nas
  // empresas que não rodaram"*).
  //
  // Medido na fonte (`cobertura_service.py:245`): `_UNIDADE_PROCESSADA` é
  // `["ok", "skip"]` — a pulada JÁ CONTA como processada, logo ela está DENTRO
  // do ✓. Em "Geração de Guias LP": ✓255 · ✕6 · ↳84 = 345 (fecha), e as ⏭161
  // são um RECORTE das 255, não uma quarta parte. Somá-las daria 506.
  //
  // Exibido com o mesmo peso dos outros, o número parecia somar — era isso que
  // confundia. Os parênteses dizem "destas, N foram puladas", e o `title` do nó
  // explica por extenso.
  //
  // O símbolo é o de "avançar faixa", escolhido pelo dono em 25/09 — é
  // literalmente o nome do status (`skip`), e não colide com ✓ ✕ ⚠ 🚚 ⏳.
  if (extra?.skip && extra.skip > 0) partes.push(`(⏭ ${extra.skip})`);
  if (running > 0) partes.push(`🚚 ${running}`);
  if (waiting && waiting > 0) partes.push(`⏳ ${waiting}`);
  if (!partes.length) return vazio ?? 'sem empresas nesta rota';
  if (total !== undefined) {
    return `${partes.join(' · ')} · ${total} empresa${total === 1 ? '' : 's'}`;
  }
  return partes.join(' · ');
};
